import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { contentAPI, GeneratedContent } from '../services/contentAPI';

const Blog: React.FC = () => {
  const [contents, setContents] = useState<GeneratedContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadContents();
  }, []);

  const loadContents = async () => {
    try {
      setLoading(true);
      const contentsData = await contentAPI.getContents();
      setContents(contentsData);
    } catch (err) {
      console.error('콘텐츠 로딩 실패:', err);
      setError('콘텐츠를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\. /g, '.').replace(/\.$/, '');
  };

  const truncateContent = (content: string, maxLength: number = 100) => {
    // HTML 태그 제거
    const textContent = content.replace(/<[^>]*>/g, '');
    if (textContent.length <= maxLength) return textContent;
    return textContent.slice(0, maxLength) + '...';
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      'AI': '#ff6b35',
      'SEO': '#4ecdc4',
      '마케팅': '#45b7d1',
      '기술': '#96ceb4',
      '환경': '#feca57',
      '식품 이야기': '#FF6900',
      '기타': '#ff9ff3'
    };
    return colors[category as keyof typeof colors] || '#6c5ce7';
  };

  const handleNewsletterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const button = form.querySelector('button') as HTMLButtonElement;
    const originalText = button.textContent || '';
    
    button.textContent = '구독 중...';
    button.disabled = true;
    
    setTimeout(() => {
      button.textContent = '구독 완료!';
      button.style.background = '#4ade80';
      
      form.reset();
      
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        button.style.background = '';
      }, 3000);
    }, 1500);
  };

  return (
    <BlogContainer>
      {loading ? (
        <LoadingSection>
          <div className="container">
            <LoadingSpinner />
            <p>컨텐츠를 불러오는 중입니다...</p>
          </div>
        </LoadingSection>
      ) : error ? (
        <ErrorSection>
          <div className="container">
            <ErrorMessage>
              <h3>오류 발생</h3>
              <p>{error}</p>
              <button onClick={loadContents}>다시 시도</button>
            </ErrorMessage>
          </div>
        </ErrorSection>
      ) : contents.length === 0 ? (
        <EmptySection>
          <div className="container">
            <EmptyState>
              <h3>컨텐츠가 없습니다</h3>
            </EmptyState>
          </div>
        </EmptySection>
      ) : (
        <BlogContentSection>
          <div className="container">
            <BlogGrid>
              {contents
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .map((content) => (
                <StyledLink key={content.id} to={`/blog/${content.id}`}>
                  <BlogCard>
                    <CardImage>
                      {content.images && content.images.length > 0 ? (
                        <img src={content.images[0].src} alt={content.images[0].alt || content.title} loading="lazy" />
                      ) : (
                        <PlaceholderImage>
                          <span>{content.category.name.charAt(0)}</span>
                        </PlaceholderImage>
                      )}
                    </CardImage>
                    <CardContent>
                      <CategoryTag color={getCategoryColor(content.category.name)}>
                        {content.category.name}
                      </CategoryTag>
                      <CardTitle>{content.title}</CardTitle>
                      <CardDescription>{truncateContent(content.content)}</CardDescription>
                      <CardMeta>
                        <AuthorInfo>
                          {content.author_bio && (
                            <AuthorImage src={content.author_bio} alt={content.author_name || '작성자'} />
                          )}
                          <div>
                            <AuthorName>{content.author_name || '쓸모'}</AuthorName>
                            <span>{formatDate(content.created_at)}</span>
                          </div>
                        </AuthorInfo>
                      </CardMeta>
                    </CardContent>
                  </BlogCard>
                </StyledLink>
              ))}
            </BlogGrid>
          </div>
        </BlogContentSection>
      )}

      {/* Newsletter Section */}
      <NewsletterSection>
        <div className="container">
          <NewsletterContent>
            <h3>쓸모 뉴스레터 구독하기</h3>
            <p>AI 기술과 환경 솔루션에 대한 최신 소식을 받아보세요</p>
            <NewsletterForm onSubmit={handleNewsletterSubmit}>
              <input 
                type="email" 
                placeholder="이메일 주소를 입력하세요" 
                required 
              />
              <button type="submit">구독하기</button>
            </NewsletterForm>
          </NewsletterContent>
        </div>
      </NewsletterSection>
    </BlogContainer>
  );
};

// Styled Components
const BlogContainer = styled.div`
  min-height: 100vh;
  background: #21004D;
`;

const LoadingSection = styled.section`
  padding: 4rem 0;
  text-align: center;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }

  p {
    margin-top: 1rem;
    color: #6b7280;
    font-size: 1.1rem;
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorSection = styled.section`
  padding: 4rem 0;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 3rem;
  text-align: center;
  max-width: 500px;
  margin: 0 auto;

  h3 {
    color: #dc2626;
    margin-bottom: 1rem;
    font-size: 1.5rem;
  }

  p {
    color: #991b1b;
    margin-bottom: 2rem;
  }

  button {
    background: #dc2626;
    color: white;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;

    &:hover {
      background: #b91c1c;
    }
  }
`;

const EmptySection = styled.section`
  padding: 4rem 0;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: #f8fafc;
  border-radius: 16px;
  max-width: 600px;
  margin: 0 auto;

  h3 {
    font-size: 1.75rem;
    color: #374151;
    margin-bottom: 1rem;
  }

  p {
    color: #6b7280;
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }
`;

const BlogContentSection = styled.section`
  padding: 4rem 0 6rem;
  position: relative;
  z-index: 1;

  .container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 2rem;
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const BlogCard = styled.article`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  max-width: 400px;
  margin: 0 auto;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

const CardImage = styled.div`
  height: 200px;
  position: relative;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const PlaceholderImage = styled.div`
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 2rem;
  font-weight: bold;
`;

const CardContent = styled.div`
  padding: 1.5rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const CategoryTag = styled.span<{ color: string }>`
  display: inline-block;
  background: ${props => props.color};
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  width: fit-content;
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  color: #1a202c;
  margin-bottom: 0.75rem;
  font-weight: 700;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardDescription = styled.p`
  color: #4a5568;
  line-height: 1.6;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.9rem;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: #718096;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  div {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
`;

const AuthorImage = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e2e8f0;
`;

const AuthorName = styled.span`
  font-weight: 600;
  color: #2d3748;
  font-size: 0.85rem;
`;

const NewsletterSection = styled.section`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  padding: 4rem 0;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }
`;

const NewsletterContent = styled.div`
  text-align: center;
  max-width: 600px;
  margin: 0 auto;

  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.95);
  }

  p {
    font-size: 1.1rem;
    opacity: 0.8;
    margin-bottom: 2rem;
    color: rgba(255, 255, 255, 0.7);
  }
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 1rem;
  max-width: 400px;
  margin: 0 auto;

  @media (max-width: 480px) {
    flex-direction: column;
  }

  input {
    flex: 1;
    padding: 0.75rem 1rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    font-size: 1rem;
    background: rgba(255, 255, 255, 0.1);
    color: white;
    backdrop-filter: blur(10px);

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }

    &:focus {
      outline: none;
      border-color: rgba(255, 255, 255, 0.4);
      box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.1);
    }
  }

  button {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.3);
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.5);
      transform: translateY(-1px);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }
`;

export default Blog;